import {
  afterNextRender,
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  output,
  signal,
  inject,
} from '@angular/core';
import { ButtonUIComponent, LoadingUIComponent } from '@shared-ui-kit';
import { ConfigService } from '@core-session';
import { registerMarkerClusterer } from '@core-tracking';
import { GoogleMap, MapMarker, MapMarkerClusterer, Renderer } from '@angular/google-maps';
import { LatLonModel, OpenAPIApiService } from '@data-access';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { SuperClusterAlgorithm } from '@googlemaps/markerclusterer';
import { BreakpointObserver } from '@angular/cdk/layout';
import { TranslatePipe } from '@ngx-translate/core';

const PICKUP_MARKER_WIDTH_PX = 40;
const PICKUP_MARKER_HEIGHT_PX = 76;
const CLUSTER_ICON_WIDTH_PX = 56;
const CLUSTER_ICON_HEIGHT_PX = 55;

@Component({
  selector: 'fr8bot-web-loads-map',
  templateUrl: './web-loads-map.html',
  styleUrl: './web-loads-map.scss',
  imports: [
    GoogleMap,
    LoadingUIComponent,
    MapMarker,
    MapMarkerClusterer,
    ButtonUIComponent,
    TranslatePipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class WebLoadsMap implements OnInit {
  /** Set on the first `tilesloaded`; the opaque loader stays up until then. */
  protected readonly tilesLoaded = signal(false);

  private config = inject(ConfigService);
  private destroyRef = inject(DestroyRef);
  private openAPIApiService = inject(OpenAPIApiService);
  private breakpointObserver = inject(BreakpointObserver);

  isMobile = signal(false);

  loadList = signal<LatLonModel[]>([]);

  isMapLoaded = signal(false);
  openCarrierCabinet = output();

  mapOptions: google.maps.MapOptions = this.config.googleMapOptionsAdvanced;
  markerOptions: google.maps.MarkerOptions = {};
  clusterRenderer: Renderer = { render: () => new google.maps.Marker() };

  clusterAlgorithm = new SuperClusterAlgorithm({
    radius: 60,
    minPoints: 3,
    minZoom: 5,
    maxZoom: 10,
  }) as any;

  constructor() {
    // The loads only feed the map, which never renders while the landing is prerendered.
    afterNextRender(() => this.getLoads());
  }

  ngOnInit() {
    this.watchScreenSize();
  }

  private watchScreenSize() {
    this.breakpointObserver
      .observe(['(max-width: 1000px)'])
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((state) => {
        this.isMobile.set(state.matches);
        this.updateMapConfigs(state.matches);
      });
  }

  private getLoads() {
    this.openAPIApiService
      .getAvailableLoads()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((res) => {
        this.loadList.set(res);
      });
  }

  onMapReady() {
    // Before the clusterer renders (it is behind `isMapLoaded`), and only in the browser: it goes
    // on `window`.
    registerMarkerClusterer();
    this.isMapLoaded.set(true);

    this.initMapMarker();
    this.initMapCluster();
  }

  private initMapMarker() {
    this.markerOptions = {
      icon: {
        url: 'assets/icons/map/map-manual-pointer.svg',
        scaledSize: new google.maps.Size(PICKUP_MARKER_WIDTH_PX, PICKUP_MARKER_HEIGHT_PX),
      },
    };
  }

  private initMapCluster() {
    this.clusterRenderer = {
      render({ count, position }) {
        return new google.maps.Marker({
          position,
          icon: {
            url: 'assets/icons/vehicle-map/unit-group.webp',
            scaledSize: new google.maps.Size(CLUSTER_ICON_WIDTH_PX, CLUSTER_ICON_HEIGHT_PX),
          },
          label: {
            text: String(count),
            color: '#000000',
            fontSize: '14px',
            fontWeight: 'bold',
          },
          zIndex: Number(google.maps.Marker.MAX_ZINDEX) + count,
        });
      },
    };
  }

  private updateMapConfigs(isMobile: boolean) {
    this.mapOptions = {
      ...this.mapOptions,
      disableDefaultUI: !isMobile,
    };
  }
}
